import { MenuItem, Popover } from '@material-ui/core';
import { createKeyPath } from 'CMFW/context/helper/keyContext';
import KeyContext from 'CMFW/context/KeyContext';
import ModuleContext from 'CMFW/context/ModuleContext';
import { THE_COLOR_OF_CHANGE } from 'components/common/styles';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { get } from 'services/request';
import styled from 'styled-components';

type IGenderOption = {
  key: string;
  use: string;
};

// Cache gender options per dialectId to avoid re-fetching
const optionsCache: Record<number, IGenderOption[]> = {};

type IProps = {
  dialectId: number;
  className?: string;
};

const VocGenderSelect: React.FC<IProps> = ({ dialectId, className }) => {
  const parentKey = useContext(KeyContext);
  const genderKey = createKeyPath(parentKey, 'gender');

  const module = useContext(ModuleContext);
  const currentGender: string = module.getItemUpdatedValue(genderKey) ?? '';
  const originalGender: string = module.getItemValue(genderKey) ?? '';

  const [options, setOptions] = useState<IGenderOption[] | null>(optionsCache[dialectId] ?? null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const chipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (optionsCache[dialectId]) {
      setOptions(optionsCache[dialectId]);
      return;
    }

    (async () => {
      const result = await get<any[]>('mcp/voc/gender-options', { dialectId });
      const dialectData = Array.isArray(result) ? result[0] : undefined;
      const opts: IGenderOption[] = [{ key: '', use: 'no gender' }];

      if (dialectData?.options) {
        for (const opt of dialectData.options) {
          opts.push({ key: opt.key, use: opt.use });
        }
      }

      optionsCache[dialectId] = opts;
      setOptions(opts);
    })();
  }, [dialectId]);

  const hasOptions = options !== null && options.length > 1;
  const loading = options === null;
  const changed = currentGender !== originalGender;

  if (!loading && !hasOptions) {
    return <Placeholder className={className} />;
  }

  const handleClick = () => {
    if (chipRef.current) {
      setAnchorEl(chipRef.current);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (key: string) => {
    module.updateItemValue.bind(module)(genderKey, key);
    setAnchorEl(null);
  };

  return (
    <Wrapper className={className}>
      <Chip ref={chipRef} $changed={changed} onClick={handleClick}>
        {currentGender || '–'}
      </Chip>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {(options ?? []).map((opt) => (
          <MenuItemStyled key={opt.key} selected={opt.key === currentGender} onClick={() => handleSelect(opt.key)}>
            {opt.key ? (
              <>
                <Key>{opt.key}</Key>
                <Use>{opt.use}</Use>
              </>
            ) : (
              <Use>{opt.use}</Use>
            )}
          </MenuItemStyled>
        ))}
      </Popover>
    </Wrapper>
  );
};

const Placeholder = styled.div`
  visibility: hidden;
  width: 50px;
  flex-shrink: 0;
`;

const Wrapper = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;

const Chip = styled.div<{ $changed: boolean }>`
  cursor: pointer;
  padding: 7px 6px;
  border: 1px solid rgba(0, 0, 0, 0.23);
  border-radius: 4px;
  font-size: 1rem;
  width: 50px;
  text-align: center;
  background-color: ${(props) => (props.$changed ? THE_COLOR_OF_CHANGE : 'transparent')};
  user-select: none;
  box-sizing: border-box;

  &:hover {
    border-color: rgba(0, 0, 0, 0.87);
  }
`;

const MenuItemStyled = styled(MenuItem)`
  && {
    font-size: 1rem;
    padding: 6px 12px;
    min-height: 32px;
  }
`;

const Key = styled.span`
  font-weight: 600;
  margin-right: 8px;
  min-width: 36px;
  display: inline-block;
`;

const Use = styled.span`
  color: #666;
`;

export default VocGenderSelect;
